import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import {
  CheckCircle2,
  Briefcase,
  TrendingUp,
  Target,
  Users,
  DollarSign,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ScrollReveal } from "./ScrollReveal";
import Header from "./Header";
import Footer from "./Footer";
import GoogleReviews from "./googleReviews";

const WealthManagement = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`/.netlify/functions/send-email`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goal: "Wealth Management",
        page_source: "wealth-management",
        message: formData.goal.trim(),
        timestamp: new Date().toISOString(),
      });

      navigate(
        `/thank-you?service=wealth-management&name=${encodeURIComponent(formData.name)}`,
      );
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Please try again or call us directly at 918.477.7227",
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#e8eef2] rounded-full text-[#3d4f5f] text-sm font-semibold mb-6">
                <Briefcase className="w-4 h-4" />
                Specialized Planning for Entrepreneurs & Business Leaders
              </div>
              <h1 className="hero-headline text-5xl sm:text-6xl font-bold text-[#3d4f5f] mb-6 leading-tight">
                Wealth Management for Oklahoma Business Owners
              </h1>
              <p className="hero-subheadline text-xl text-gray-600 mb-10">
                Integrated financial strategies designed for entrepreneurs,
                founders, and business leaders.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() =>
                    document
                      .getElementById("consultation-form")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  className="btn-primary btn-cta-pulse bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
                >
                  Schedule a No-Cost Strategy Call
                </Button>
                <Button
                  variant="outline"
                  className="sm:hidden border-2 border-[#3d4f5f] text-[#3d4f5f] px-8 py-6 text-lg rounded-lg hover:bg-[#f8fafb] transition-all duration-200"
                  onClick={() => (window.location.href = "tel:+19184777227")}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call a Local Advisor
                </Button>
              </div>
              <div className="hero-trust-badges flex flex-wrap justify-center lg:justify-start gap-8 mt-12 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Business-Owner Focused</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Fiduciary Advisors</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Oklahoma-Based Firm</span>
                </div>
              </div>

              {/* <GoogleReviews />*/}
            </div>

            {/* Right Image */}
            <div className="hidden lg:block hero-cta">
              <img
                src="https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Business owner wealth management planning"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Business Owners Face */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Challenges Business Owners Face
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Running a business creates unique financial complexities that
                require specialized planning
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <TrendingUp className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Inconsistent Income
                </h3>
                <p className="text-gray-600">
                  Variable cash flow requires careful planning and strategic
                  reserve management.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <DollarSign className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Tax Complexity
                </h3>
                <p className="text-gray-600">
                  Business tax strategies require coordination with personal
                  financial planning.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <Target className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Exit Planning
                </h3>
                <p className="text-gray-600">
                  Most business wealth is locked in the business, requiring
                  succession planning.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <Briefcase className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Personal vs Business
                </h3>
                <p className="text-gray-600">
                  Balancing business reinvestment with personal wealth building
                  is critical.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Section */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Business Owner Wealth Services
              </h2>
              <p className="text-lg text-gray-600">
                Comprehensive strategies that integrate your business and
                personal finances
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                {
                  title: "Cash Flow & Investment Planning",
                  desc: "Strategic management of irregular income and investment opportunities",
                },
                {
                  title: "Tax-Efficient Wealth Strategies",
                  desc: "Coordinate business and personal tax planning to minimize overall tax burden",
                },
                {
                  title: "Business Succession Planning",
                  desc: "Develop a clear path for transferring or selling your business",
                },
                {
                  title: "Retirement Plans for Owners & Employees",
                  desc: "Design retirement programs that benefit you and attract talent",
                },
                {
                  title: "Exit Planning & Maximization",
                  desc: "Prepare your business for sale and optimize after-tax proceeds",
                },
                {
                  title: "Risk Management & Insurance",
                  desc: "Protect your business and personal assets with proper coverage",
                },
              ].map((service, index) => (
                <Card key={index} className="card-hover p-8 bg-white">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#4a6274] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-bold text-[#3d4f5f] mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600">{service.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-12">
              <Button
                onClick={() =>
                  document
                    .getElementById("consultation-form")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="bg-[#4a6274] hover:bg-[#3a5263] text-white px-8 py-6 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl hover:scale-105"
              >
                Start Your Business Wealth Plan
              </Button>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* How We Help */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                How We Help Business Owners
              </h2>
              <p className="text-lg text-gray-600">
                A structured approach designed specifically for entrepreneurs
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Business & Personal Financial Review",
                  desc: "Comprehensive analysis of both your business and personal financial situation",
                },
                {
                  step: "2",
                  title: "Integrated Planning Strategy",
                  desc: "Develop coordinated strategies that align business goals with personal wealth objectives",
                },
                {
                  step: "3",
                  title: "Long-Term Optimization",
                  desc: "Ongoing guidance to adapt your plan as your business grows and evolves",
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 bg-[#3d4f5f] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Social Proof */}
      {/* <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Trusted by Oklahoma Business Owners
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="card-hover p-8 bg-white">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://ui-avatars.com/api/?name=Chris+N&rounded=true&bold=true&format=svg"
                    alt="Client testimonial"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4a6274]" />
                      <span className="font-semibold text-[#3d4f5f]">
                        Chris N
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "Over the last 35 years, Curt has demonstrated the flexibility
                  and dynamic knowledge of financial markets to turn what
                  appears to be a questionable situation into success at age 54.
                  I have doubts that I could have done this at all without his
                  help. I recommend him without reservation."
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://ui-avatars.com/api/?name=Pam+S&rounded=true&bold=true&format=svg"
                    alt="Client testimonial"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4a6274]" />
                      <span className="font-semibold text-[#3d4f5f]">
                        Pam S
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "Curt Wilson has been my financial advisor/planner for about
                  6.5 years. During that time I have seen the care he takes with
                  my investments and appreciate greatly the time he spends with
                  me explaining his thought processes and answering all my
                  questions. His education, professionalism and personal sense
                  of responsibility help me feel confident in all my dealings
                  with him."
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal> */}

      {/* Consultation Form */}
      <section
        id="consultation-form"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              Build Wealth Beyond Your Business
            </h2>
            <p className="text-lg text-gray-600">
              Speak with an Oklahoma wealth advisor for business owners today
            </p>
          </div>
          <Card className="p-8 shadow-xl border-2 border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="918.477.7227"
                />
              </div>
              <div>
                <label
                  htmlFor="goal"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Primary Wealth Goal *
                </label>
                <Input
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                  placeholder="e.g., Exit planning, Tax optimization, Retirement planning"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-6 text-lg font-semibold rounded-lg shadow-lg transition-all hover:shadow-xl hover:scale-105 disabled:opacity-50"
              >
                {isSubmitting
                  ? "Submitting..."
                  : "Schedule a No-Cost Strategy Call"}
              </Button>
              <p className="text-sm text-gray-500 text-center">
                By submitting, you agree to be contacted about wealth management
                services. No obligation.
              </p>
            </form>
          </Card>
        </div>
      </section>

      <Footer />

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg md:hidden z-40">
        <Button
          onClick={() =>
            document
              .getElementById("consultation-form")
              .scrollIntoView({ behavior: "smooth" })
          }
          className="w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-4 text-base font-semibold rounded-lg shadow-lg"
        >
          Schedule a No-Cost Strategy Call
        </Button>
      </div>
    </div>
  );
};

export default WealthManagement;
